'use strict';

import { buildPipelineStatusGraphStagesFromFlow } from './pipelineStatusGraphBuilders';
import {
	requirePipelineStatusFlowScenarioKey,
	SINGLE_CORE_PROJECT_FLOW_SCENARIO,
	SINGLE_V7_PROJECT_FLOW_SCENARIO,
} from './pipelineStatusFlowScenarios';
import { PipelineStatusWorkflowNames } from './pipelineStatusTypes';

/**
 * Core sample-set mock flow graph for scenario {@link SINGLE_CORE_PROJECT_FLOW_SCENARIO}. Node list is empty until stages are authored; mock fact rows may still exist for API tests.
 *
 * @type {import('./pipelineStatusTypes').PipelineStatusGraphDefinition}
 */
const singleCoreSampleSetFlowGraph = {
	id: 'single-core-project-flow',
	stages: buildPipelineStatusGraphStagesFromFlow(
		{
			scope: 'Project',
			workflow: PipelineStatusWorkflowNames.Publishing,
			pipeline: 'Core',
		},
		[],
	),
};

/**
 * V7 / Project / Publishing UX DAG (`singleV7ProjectFlow`). Graph id `single-v7-project-flow` pairs with that scenario key.
 *
 * @type {import('./pipelineStatusTypes').PipelineStatusGraphDefinition}
 */
/** Manifest match for V7 poller sample-set fan-out (see mocks `SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS`). */
const V7_SAMPLE_SET_ITERATION_MANIFEST_MATCH = Object.freeze({
	parentStage: 'V7ProjectPoller',
	iterationDimension: 'SampleSetId',
	parentService: 'V7ProductService',
});

const V7_SAMPLE_SET_MANIFEST_COMPLETION_POLICY = 'allIterationsSucceeded';

const singleV7ProjectFlowGraph = {
	id: 'single-v7-project-flow',
	stages: buildPipelineStatusGraphStagesFromFlow(
		{
			scope: 'Project',
			workflow: PipelineStatusWorkflowNames.Publishing,
			pipeline: 'V7',
		},
		[
			{
				id: 'v7-project-poller',
				service: 'V7ProductService',
				description: 'Publishing trigger activated from mlims',
				dependsOn: [],
				producerStage: 'V7ProjectPoller',
			},
			{
				id: 'v7-project-available',
				service: 'PortalApi',
				description: 'Transferring files from mlims',
				dependsOn: ['v7-project-poller'],
				producerStage: 'V7ProjectAvailable',
			},
			{
				id: 'v7-sample-set-available',
				service: 'V7ProductService',
				description: 'Transferring sample set from mlims',
				dependsOn: ['v7-project-poller'],
				producerStage: 'V7SampleSetAvailable',
				iterationManifestMatch: V7_SAMPLE_SET_ITERATION_MANIFEST_MATCH,
				completionPolicy: V7_SAMPLE_SET_MANIFEST_COMPLETION_POLICY,
			},
			{
				id: 'v7-product-sample-set-biovizservice',
				service: 'BioVizService',
				description: 'Sending mlims sample set to IBP',
				dependsOn: ['v7-sample-set-available'],
				producerStage: 'ProductSampleSetAvailable',
				iterationManifestMatch: V7_SAMPLE_SET_ITERATION_MANIFEST_MATCH,
				completionPolicy: V7_SAMPLE_SET_MANIFEST_COMPLETION_POLICY,
			},
			{
				id: 'v7-product-sample-set-portalapi',
				service: 'PortalApi',
				description: 'Storing mlims sample set in database',
				dependsOn: ['v7-sample-set-available'],
				producerStage: 'ProductSampleSetAvailable',
				iterationManifestMatch: V7_SAMPLE_SET_ITERATION_MANIFEST_MATCH,
				completionPolicy: V7_SAMPLE_SET_MANIFEST_COMPLETION_POLICY,
			},
			{
				id: 'v7-product-sample-set-pathwayexplorerapi',
				service: 'PathwayExplorerApi',
				description: 'Storing mlims sample set in database',
				dependsOn: ['v7-sample-set-available'],
				producerStage: 'ProductSampleSetAvailable',
				iterationManifestMatch: V7_SAMPLE_SET_ITERATION_MANIFEST_MATCH,
				completionPolicy: V7_SAMPLE_SET_MANIFEST_COMPLETION_POLICY,
			},
		],
	),
};

/**
 * Registered graph definitions keyed by the same strings as mock scenarios (`singleV7ProjectFlow`, `singleCoreProjectFlow`).
 *
 * @type {Readonly<Record<string, import('./pipelineStatusTypes').PipelineStatusGraphDefinition>>}
 */
export const PIPELINE_STATUS_GRAPH_DEFINITIONS_BY_FLOW = Object.freeze({
	[SINGLE_V7_PROJECT_FLOW_SCENARIO]: singleV7ProjectFlowGraph,
	[SINGLE_CORE_PROJECT_FLOW_SCENARIO]: singleCoreSampleSetFlowGraph,
});

/**
 * @param {unknown} flowKey Scenario key (e.g. {@link SINGLE_V7_PROJECT_FLOW_SCENARIO}).
 * @returns {import('./pipelineStatusTypes').PipelineStatusGraphDefinition}
 */
export function getPipelineStatusGraphDefinitionForFlow(flowKey) {
	const key = requirePipelineStatusFlowScenarioKey(flowKey);
	const def = PIPELINE_STATUS_GRAPH_DEFINITIONS_BY_FLOW[key];
	if (!def) {
		throw new Error(`[pipelineStatus] No graph registered for flow ${JSON.stringify(key)}`);
	}
	return def;
}
